'use client';

import { useLink } from '@react-aria/link';
import { type ElementType, useRef } from 'react';

import { disableInteractions } from '../../../internal/utils/disableInteractions';
import { filterProps } from '../../../internal/utils/filterProps';
import { mergeProps } from '../../../utils/mergeProps';
import { LinkBase, type LinkProps } from './LinkBase';

// `useLink` branch: the default anchor, a string `component` other than
// `button` (e.g. `'div'`), or a custom component (router/`next` `Link`).
const LinkAsAnchor = (props: LinkProps) => {
  const [localProps, linkRaProps] = filterProps(props, ['useLink']);

  const { role, tabIndex, ...restLocalProps } = localProps;

  const { isDisabled, component } = props;

  const Root = (component || 'a') as ElementType;

  const internalRef = useRef<HTMLElement>(null);

  // ZDS-API-DOM-008: a string `component` passes straight through as the
  // `elementType`; a custom component only resolves to `'a'` when `to`/`href`
  // signal it renders an anchor at runtime. `useLink`'s `elementType` accepts a
  // string tag only, so a custom component without those nav props can't be
  // forwarded — omit it and let React Aria assume a native anchor (its default).
  // See `ListItemButton.tsx`.
  const isLinkLike = 'href' in props || 'to' in props;
  const elementType: string | undefined =
    typeof Root === 'string' ? Root : isLinkLike ? 'a' : undefined;

  const { linkProps, isPressed } = useLink(
    { ...linkRaProps, elementType },
    internalRef
  );

  const elementProps = {
    ...disableInteractions(mergeProps(restLocalProps, linkProps), {
      isDisabled,
    }),
    role: isDisabled && Root === 'a' ? 'link' : role,
    tabIndex: isDisabled ? -1 : tabIndex,
  };

  return (
    <LinkBase
      {...elementProps}
      Root={Root}
      internalRef={internalRef}
      isPressed={isPressed}
      isDisabled={isDisabled}
    />
  );
};

LinkAsAnchor.displayName = 'LinkAsAnchor';

export { LinkAsAnchor };
